import React, { FC, useEffect, useMemo, useRef } from 'react';
import cx from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import { ReactComponent as UploadIcon } from 'assets/icons/upload-lg.svg';
import 'swiper/css';
import 'swiper/css/pagination';
import styles from './MediaSlider.module.scss';

export type MediaItem = string | File; // string = existing URL, File = new upload

interface MediaSliderProps {
	mediaItems: MediaItem[];
	editMode?: boolean;
	autoplayDelay?: number; // in seconds
	showUploadSlide?: boolean;
	onUploadClick?: () => void;
	onRemoveMedia?: (index: number) => void;
	className?: string;
	slideClassName?: string;
}

export const MediaSlider: FC<MediaSliderProps> = ({
	mediaItems,
	editMode = false,
	autoplayDelay = 10,
	showUploadSlide = false,
	onUploadClick,
	onRemoveMedia,
	className,
	slideClassName,
}) => {
	const filePreviewUrlRefs = useRef<Map<number, string>>(new Map());
	const fileTypeRefs = useRef<Map<number, 'image' | 'video'>>(new Map());

	// Build media list with preview URLs for files
	const mediaList = useMemo(() => {
		// Clean up old preview URLs
		filePreviewUrlRefs.current.forEach((url, idx) => {
			if (idx >= mediaItems.length || !(mediaItems[idx] instanceof File)) {
				if (url.startsWith('blob:')) {
					URL.revokeObjectURL(url);
				}
				filePreviewUrlRefs.current.delete(idx);
				fileTypeRefs.current.delete(idx);
			}
		});

		return mediaItems.map((item, index) => {
			if (item instanceof File) {
				// Check if we need to create a new preview URL (file changed)
				const isVideo = item.type === 'video/mp4' || item.name.toLowerCase().endsWith('.mp4');
				const currentType = fileTypeRefs.current.get(index);
				const needsNewUrl =
					!filePreviewUrlRefs.current.has(index) || currentType !== (isVideo ? 'video' : 'image');

				if (needsNewUrl) {
					// Clean up old URL if it exists
					const oldUrl = filePreviewUrlRefs.current.get(index);
					if (oldUrl && oldUrl.startsWith('blob:')) {
						URL.revokeObjectURL(oldUrl);
					}
					// Create new preview URL
					const previewUrl = URL.createObjectURL(item);
					filePreviewUrlRefs.current.set(index, previewUrl);
					fileTypeRefs.current.set(index, isVideo ? 'video' : 'image');
				}
				return filePreviewUrlRefs.current.get(index) || '';
			}
			// For existing URL strings, determine type from URL
			const isVideoUrl = item.toLowerCase().endsWith('.mp4') || item.includes('.mp4');
			fileTypeRefs.current.set(index, isVideoUrl ? 'video' : 'image');
			return item;
		});
	}, [mediaItems]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			filePreviewUrlRefs.current.forEach(url => {
				if (url.startsWith('blob:')) {
					URL.revokeObjectURL(url);
				}
			});
			filePreviewUrlRefs.current.clear();
			fileTypeRefs.current.clear();
		};
	}, []);

	// Show upload slide if no media and showUploadSlide is true
	const shouldShowUploadSlide = showUploadSlide && mediaList.length === 0;

	return (
		<div
			className={cx(styles.mediaSlider, className, {
				[styles.mediaSlider_edit]: editMode,
			})}
		>
			<Swiper
				modules={[Pagination, Autoplay]}
				pagination={{ clickable: true }}
				loop={mediaList.length > 1}
				autoplay={
					mediaList.length > 0
						? {
								delay: autoplayDelay * 1000,
								disableOnInteraction: false,
						  }
						: false
				}
				speed={600}
				spaceBetween={30}
				grabCursor={true}
			>
				{/* Media slides */}
				{mediaList.map((src, index) => {
					const mediaType = fileTypeRefs.current.get(index) || 'image';
					const isVideoFile = mediaType === 'video';

					return (
						<SwiperSlide key={`media-${index}`}>
							<div
								className={cx(styles.mediaSlider__slide_wrapper, slideClassName, {
									[styles.mediaSlider__slide_wrapper_edit]: editMode,
								})}
							>
								{isVideoFile ? (
									<video
										className={styles.mediaSlider__video}
										src={src}
										autoPlay
										loop
										muted
										playsInline
									/>
								) : (
									<img className={styles.mediaSlider__image} src={src} alt={`Slide ${index + 1}`} />
								)}
								{editMode && onRemoveMedia && (
									<div
										className={styles.mediaSlider__slide_hover}
										onClick={() => onRemoveMedia(index)}
									/>
								)}
							</div>
						</SwiperSlide>
					);
				})}
				{/* Upload slide */}
				{shouldShowUploadSlide && onUploadClick && (
					<SwiperSlide key="upload-slide">
						<div className={styles.mediaSlider__upload_slide} onClick={onUploadClick}>
							<UploadIcon className={styles.mediaSlider__upload_icon} />
							<span className={styles.mediaSlider__upload_text}>Upload New Media</span>
						</div>
					</SwiperSlide>
				)}
			</Swiper>
		</div>
	);
};
